/**
 * @file BaseDockEvents.ts
 * @description 底座父子设备事件契约。
 * 底座相关 device.emit/on/off 都应优先引用这里的常量，Web 层也通过这里了解事件流。
 */

import type { BaseDevice } from '../runtime/BaseDevice'
import type { BaseChildReport, BaseConnectedMouse } from '../types/base'

export const BaseDockEvents = {
  /** 底座读到已配对子鼠标 VID/PID；此时不一定新建了子设备实例。 */
  ChildIdentityConnected: 'base:child-connected',
  /** 底座确认子鼠标链路断开；子设备是否移除由具体场景决定。 */
  ChildIdentityDisconnected: 'base:child-disconnected',
  /** SDK 已经基于子鼠标 manifest 创建真实鼠标驱动，并挂到 base.subDevices。 */
  ChildDeviceConnected: 'base:child-device-connected',
  /** SDK 已经从 base.subDevices 移除逻辑子设备。 */
  ChildDeviceDisconnected: 'base:child-device-disconnected',
  /** 子鼠标在线态变化；配对关系仍保留，不等于从展示列表删除。 */
  ChildOnlineChanged: 'base:child-online-changed',
  /** 父设备的展示子设备列表发生变化，Web 层应重新派生展示列表。 */
  ChildDisplayChanged: 'base:child-display-changed',
  /** 底座 0xb0 子鼠标状态上报，包含 DPI、电量、回报率等解析结果。 */
  ChildReport: 'base:child-report',
  /** 底座屏幕素材上传进度。 */
  ScreenUploadProgress: 'base:screen-upload-progress',
} as const

export type BaseDockEventName = (typeof BaseDockEvents)[keyof typeof BaseDockEvents]

export type BaseDockEventPayloadMap = {
  [BaseDockEvents.ChildIdentityConnected]: BaseConnectedMouse
  [BaseDockEvents.ChildIdentityDisconnected]: BaseDevice | undefined
  [BaseDockEvents.ChildDeviceConnected]: BaseDevice
  [BaseDockEvents.ChildDeviceDisconnected]: BaseDevice
  [BaseDockEvents.ChildOnlineChanged]: {
    device: BaseDevice
    online: boolean
  }
  [BaseDockEvents.ChildDisplayChanged]: BaseDevice[] | undefined
  [BaseDockEvents.ChildReport]: BaseChildReport
  [BaseDockEvents.ScreenUploadProgress]: {
    progress: number
    frameIndex?: number
  }
}
